'use client';

import React, { Fragment, useState, useRef } from 'react';
import { RadioGroup } from '@headlessui/react';
import clx from 'classnames';

import useFormatSortingOptions from './hooks/useFormatSortingOptions';
import { SortMethods } from '../../../../modules/sortAndFilter/config/SortAndFilterConfig';
import { SelectSortCommand } from '../../../../modules/sortAndFilter/patterns/commands/googleAnalytics';
import { gaEvents } from '../../../../modules/analytics/google/dispatchGoogleAnalyticsEvent';
import { useTreatmentSortStateContext, useTreatmentSortUpdaterContext } from './context/TreatmentSortContext';
import { useSortAndFilterConfigContext } from '../context/SortAndFilterConfigContext';

import SortAndFilterDialog from '../SortAndFilterDialog/SortAndFilterDialog';
import Button from '../../../_ui/_blocks/Buttons/Button/Button';
import SortingOption, { sortMethodLabels } from './SortingOption/SortingOption';

import styles from './TreatmentSort.module.css';
import Icon from '../../../_ui/_blocks/Icon/Icon';

interface Props {
    className?: string;
}

const TreatmentSort = ({ className }: Props) => {
    const classNames = clx(styles.button, className);

    const sortMethod = useTreatmentSortStateContext();
    const setSortMethod = useTreatmentSortUpdaterContext();
    const config = useSortAndFilterConfigContext();

    const formattedSortingOptions = useFormatSortingOptions(config);

    const [isOpen, setIsOpen] = useState(false);
    const handleClose = () => setIsOpen(false);
    const handleOpen = () => setIsOpen(true);

    const hasBeenInteractedWith = useRef(false);

    const handleSortByChange = (sorting: SortMethods) => {
        setSortMethod(sorting);

        new SelectSortCommand().execute({
            event: gaEvents.SortUsage,
            sortSelection: sortMethodLabels[sorting],
            list: window.location.pathname,
        });

        hasBeenInteractedWith.current = true;

        handleClose();
    };

    return (
        <Fragment>
            <SortAndFilterDialog isOpen={isOpen} onClose={handleClose} title="Sort By">
                <RadioGroup value={sortMethod} onChange={handleSortByChange} as="ul" className="space-y-200 px-100 py-200">
                    {formattedSortingOptions.map((sortMethod) =>
                        sortMethodLabels[sortMethod] ? <SortingOption key={sortMethod} sortMethod={sortMethod} /> : null
                    )}
                </RadioGroup>
            </SortAndFilterDialog>
            <Button
                variant="tertiary"
                size="micro"
                onClick={handleOpen}
                title="Change sorting"
                className={`${classNames} md:button-size-small`}
                data-testid="treatment-sort-button"
            >
                {hasBeenInteractedWith.current ? sortMethodLabels[sortMethod] : 'Sort By'}
                <Icon icon="sorted-default-light" alt="sorted" size="xsmall" />
            </Button>
        </Fragment>
    );
};

export default TreatmentSort;
